import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import VolunteerNav from '../components/VolunteerNav';

const Documents = () => {
  const { user, logout } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [fileTypeFilter, setFileTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' is the primary/default view

  // Folder state
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null); // null = root level

  // Document preview modal state
  const [documentPreview, setDocumentPreview] = useState({
    isOpen: false,
    document: null,
    loading: false
  });

  // Upload form state
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    category: 'general',
    folder_id: ''
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');

  useEffect(() => {
    fetchDocuments();
    fetchFolders();
  }, []);

  const fetchFolders = async () => {
    try {
      const response = await api.get('/folders');
      if (response.data.success) {
        setFolders(response.data.folders);
      }
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/documents');

      if (response.data.success) {
        setDocuments(response.data.documents);
      } else {
        setError(response.data.message || 'Failed to load documents');
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      setUploadError('Please select a file.');
      return;
    }
    if (!uploadForm.title.trim()) {
      setUploadError('Title is required.');
      return;
    }

    setUploading(true);
    setUploadError('');
    setUploadSuccess('');

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', uploadForm.title.trim());
      formData.append('description', uploadForm.description.trim());
      formData.append('category', uploadForm.category);
      if (uploadForm.folder_id) formData.append('folder_id', uploadForm.folder_id);

      await api.post('/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setUploadSuccess('Document uploaded successfully!');
      setUploadForm({ title: '', description: '', category: 'general', folder_id: '' });
      setUploadFile(null);
      // Reset file input
      const fileInput = document.getElementById('vol-file-input');
      if (fileInput) fileInput.value = '';
      setShowUploadForm(false);
      fetchDocuments();
    } catch (err) {
      setUploadError(err.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId, docTitle) => {
    if (!window.confirm(`Delete "${docTitle}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/documents/${docId}`);
      fetchDocuments();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete document.');
    }
  };

  const handleDownload = async (documentId, fileName) => {
    try {
      const response = await api.get(`/documents/${documentId}/download`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      setError('Failed to download document');
    }
  };

  // Handle document preview
  const handlePreviewDocument = async (doc) => {
    // Embedded documents don't need a blob fetch
    if (doc.document_type === 'embedded') {
      setDocumentPreview({
        isOpen: true,
        document: doc,
        loading: false,
        previewUrl: null
      });
      return;
    }

    setDocumentPreview({
      isOpen: true,
      document: doc,
      loading: true,
      previewUrl: null
    });

    // Fetch document with authentication to create preview URL
    try {
      const response = await api.get(`/documents/${doc.id}/download`, {
        responseType: 'blob',
      });

      // Create object URL from blob
      const blob = new Blob([response.data], { type: doc.file_type });
      const previewUrl = window.URL.createObjectURL(blob);

      setDocumentPreview(prev => ({
        ...prev,
        loading: false,
        previewUrl: previewUrl
      }));
    } catch (error) {
      console.error('Error generating preview:', error);
      setDocumentPreview(prev => ({
        ...prev,
        loading: false,
        previewUrl: null
      }));
    }
  };

  // Close document preview modal
  const closeDocumentPreview = () => {
    // Clean up object URL to prevent memory leaks
    if (documentPreview.previewUrl) {
      window.URL.revokeObjectURL(documentPreview.previewUrl);
    }

    setDocumentPreview({
      isOpen: false,
      document: null,
      loading: false,
      previewUrl: null
    });
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getFileExtension = (doc) => {
    if (doc.document_type === 'embedded') return 'LINK';
    const name = doc.file_name || '';
    const ext = name.includes('.') ? name.split('.').pop() : '';
    return ext ? ext.toUpperCase().slice(0, 4) : 'FILE';
  };

  const getFileIconColor = (doc) => {
    if (doc.document_type === 'embedded') return 'bg-indigo-100 text-indigo-600';
    const colors = {
      pdfs: 'bg-red-100 text-red-600',
      docs: 'bg-blue-100 text-blue-600',
      spreadsheets: 'bg-green-100 text-green-600',
      images: 'bg-purple-100 text-purple-600',
      other: 'bg-gray-100 text-gray-600'
    };
    return colors[getFileTypeCategory(doc.file_type)] || colors.other;
  };

  const PinIcon = ({ className = 'w-3.5 h-3.5' }) => (
    <svg className={className} fill="currentColor" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
    </svg>
  );

  const getCategoryColor = (category) => {
    const colors = {
      'policy': 'bg-red-100 text-red-800',
      'training': 'bg-blue-100 text-blue-800',
      'forms': 'bg-green-100 text-green-800',
      'resources': 'bg-purple-100 text-purple-800',
      'guidelines': 'bg-yellow-100 text-yellow-800',
      'general': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors.general;
  };

  const getVisibilityColor = (visibility) => {
    const colors = {
      'all': 'bg-green-100 text-green-800',
      'volunteers_only': 'bg-blue-100 text-blue-800'
    };
    return colors[visibility] || colors.all;
  };

  const getFileTypeCategory = (fileType) => {
    if (!fileType) return 'embedded';
    if (fileType.includes('pdf')) return 'pdfs';
    if (fileType.includes('word') || fileType.includes('text')) return 'docs';
    if (fileType.includes('excel') || fileType.includes('sheet')) return 'spreadsheets';
    if (fileType.includes('image')) return 'images';
    return 'other';
  };

  const convertToEmbedUrl = (url) => {
    try {
      const urlObj = new URL(url);
      // SharePoint URLs
      if (urlObj.hostname.includes('sharepoint.com')) {
        urlObj.searchParams.set('action', 'embedview');
        return urlObj.toString();
      }
      // OneDrive personal
      if (urlObj.hostname.includes('onedrive.live.com')) {
        urlObj.pathname = urlObj.pathname.replace('/redir', '/embed').replace('/view', '/embed');
        return urlObj.toString();
      }
      // Google Sheets
      if (urlObj.hostname.includes('docs.google.com') && urlObj.pathname.includes('/spreadsheets/')) {
        return url.replace(/\/(edit|view)(#.*)?(\?.*)?$/, '/pubhtml');
      }
      // Google Docs
      if (urlObj.hostname.includes('docs.google.com') && urlObj.pathname.includes('/document/')) {
        return url.replace(/\/(edit|view)(#.*)?(\?.*)?$/, '/pub?embedded=true');
      }
      // Google Slides
      if (urlObj.hostname.includes('docs.google.com') && urlObj.pathname.includes('/presentation/')) {
        return url.replace(/\/(edit|view)(#.*)?(\?.*)?$/, '/embed');
      }
      // Google Drive file preview
      if (urlObj.hostname.includes('drive.google.com') && urlObj.pathname.includes('/file/')) {
        const fileId = urlObj.pathname.match(/\/d\/([^/]+)/)?.[1];
        if (fileId) return `https://drive.google.com/file/d/${fileId}/preview`;
      }
      return url;
    } catch {
      return url;
    }
  };

  const [passwordCopied, setPasswordCopied] = useState(false);

  const handleCopyPassword = (password) => {
    navigator.clipboard.writeText(password);
    setPasswordCopied(true);
    setTimeout(() => setPasswordCopied(false), 2000);
  };

  // Filter documents by selected folder first
  const documentsInCurrentFolder = documents.filter(doc => {
    if (selectedFolder === null) {
      return !doc.folder_id; // Root level - only docs without folder
    } else {
      return doc.folder_id === selectedFolder; // Specific folder
    }
  });

  const filteredDocuments = documentsInCurrentFolder.filter(doc => {
    const categoryMatch = filter === 'all' || doc.category === filter;
    const fileTypeMatch = fileTypeFilter === 'all' || getFileTypeCategory(doc.file_type) === fileTypeFilter;
    const searchMatch = !search ||
      doc.title.toLowerCase().includes(search.toLowerCase()) ||
      (doc.description || '').toLowerCase().includes(search.toLowerCase());
    return categoryMatch && fileTypeMatch && searchMatch;
  });

  const categories = [...new Set(documentsInCurrentFolder.map(doc => doc.category))];

  const fileTypeCounts = {
    all: documentsInCurrentFolder.length,
    pdfs: documentsInCurrentFolder.filter(d => getFileTypeCategory(d.file_type) === 'pdfs').length,
    docs: documentsInCurrentFolder.filter(d => getFileTypeCategory(d.file_type) === 'docs').length,
    spreadsheets: documentsInCurrentFolder.filter(d => getFileTypeCategory(d.file_type) === 'spreadsheets').length,
    images: documentsInCurrentFolder.filter(d => getFileTypeCategory(d.file_type) === 'images').length
  };

  // Get current folder name for breadcrumb
  const currentFolderName = selectedFolder
    ? folders.find(f => f.id === selectedFolder)?.name || 'Folder'
    : 'All Documents';

  if (loading) {
    return (
      <div className="h-screen bg-gray-50 flex overflow-hidden">
        <VolunteerNav currentPage="Documents" />

        <main className="flex-1 overflow-y-auto pt-16 lg:pt-0 h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading documents...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <VolunteerNav currentPage="Documents" />

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto pt-16 lg:pt-0 h-screen">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
            <button
              onClick={() => setError('')}
              className="float-right text-red-700 hover:text-red-900"
            >
              ×
            </button>
          </div>
        )}

        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Documents</h2>
            <p className="text-gray-600">Access and download organization documents</p>
          </div>
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search documents..."
                className="pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-48"
              />
            </div>
            <div className="flex items-center gap-1 bg-gray-100 rounded-md p-1">
              <button
                onClick={() => setViewMode('list')}
                title="List view"
                className={`p-1.5 rounded transition-colors ${viewMode === 'list' ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
              </button>
              <button
                onClick={() => setViewMode('grid')}
                title="Grid view"
                className={`p-1.5 rounded transition-colors ${viewMode === 'grid' ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
              </button>
            </div>
            <button
              onClick={() => { setShowUploadForm(f => !f); setUploadError(''); setUploadSuccess(''); }}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap"
            >
              {showUploadForm ? 'Cancel Upload' : '+ Upload Document'}
            </button>
          </div>
        </div>

        {/* Upload success banner */}
        {uploadSuccess && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {uploadSuccess}
            <button onClick={() => setUploadSuccess('')} className="float-right text-green-700 hover:text-green-900">×</button>
          </div>
        )}

        {/* Upload Form */}
        {showUploadForm && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Document</h3>
            {uploadError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">{uploadError}</div>
            )}
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={e => setUploadForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Document title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">File <span className="text-red-500">*</span></label>
                <input
                  id="vol-file-input"
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
                  onChange={e => setUploadFile(e.target.files[0] || null)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">Max 10 MB. Accepted: PDF, Word, Excel, text, images.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={uploadForm.category}
                    onChange={e => setUploadForm(f => ({ ...f, category: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="general">General</option>
                    <option value="policy">Policy</option>
                    <option value="training">Training</option>
                    <option value="forms">Forms</option>
                    <option value="resources">Resources</option>
                    <option value="guidelines">Guidelines</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Folder (optional)</label>
                  <select
                    value={uploadForm.folder_id}
                    onChange={e => setUploadForm(f => ({ ...f, folder_id: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Root (no folder)</option>
                    {folders.map(folder => (
                      <option key={folder.id} value={folder.id}>{folder.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <textarea
                  value={uploadForm.description}
                  onChange={e => setUploadForm(f => ({ ...f, description: e.target.value }))}
                  rows={2}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Brief description of this document"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={uploading}
                  className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white px-5 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowUploadForm(false); setUploadError(''); }}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-5 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Main Content with Folder Sidebar */}
        <div className="flex gap-6">
          {/* Folder Sidebar */}
          {folders.length > 0 && (
            <div className="w-56 flex-shrink-0 hidden md:block">
              <div className="bg-white rounded-lg shadow p-4 sticky top-4">
                <h3 className="font-semibold text-gray-900 mb-4">Folders</h3>

                {/* Root level (All Documents) */}
                <button
                  onClick={() => setSelectedFolder(null)}
                  className={`w-full text-left px-3 py-2 rounded-md mb-1 transition-colors text-sm ${
                    selectedFolder === null
                      ? 'bg-blue-50 text-blue-700 font-medium'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  Root Documents
                  <span className="ml-2 text-xs text-gray-500">
                    ({documents.filter(d => !d.folder_id).length})
                  </span>
                </button>

                {/* Folders list */}
                <div className="space-y-1 mt-2">
                  {folders.map(folder => (
                    <button
                      key={folder.id}
                      onClick={() => setSelectedFolder(folder.id)}
                      className={`w-full text-left px-3 py-2 rounded-md transition-colors text-sm ${
                        selectedFolder === folder.id
                          ? 'bg-blue-50 text-blue-700 font-medium'
                          : 'hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      {folder.name}
                      <span className="ml-2 text-xs text-gray-500">
                        ({documents.filter(d => d.folder_id === folder.id).length})
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Documents Content Area */}
          <div className="flex-1 min-w-0">
            {/* Mobile Folder Selector */}
            {folders.length > 0 && (
              <div className="md:hidden bg-white rounded-lg shadow p-4 mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Folder</label>
                <select
                  value={selectedFolder || ''}
                  onChange={(e) => setSelectedFolder(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Root Documents ({documents.filter(d => !d.folder_id).length})</option>
                  {folders.map(folder => (
                    <option key={folder.id} value={folder.id}>
                      {folder.name} ({documents.filter(d => d.folder_id === folder.id).length})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Current Location Breadcrumb */}
            {selectedFolder && (
              <div className="bg-white rounded-lg shadow p-3 mb-4 flex items-center text-sm">
                <button
                  onClick={() => setSelectedFolder(null)}
                  className="text-blue-600 hover:text-blue-700"
                >
                  Documents
                </button>
                <span className="mx-2 text-gray-400">/</span>
                <span className="text-gray-900 font-medium">{currentFolderName}</span>
              </div>
            )}

        {/* Filter Bar */}
        {categories.length > 1 && (
          <div className="bg-white rounded-lg shadow p-4 mb-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Filter by Category</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  filter === 'all'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                All Categories ({documentsInCurrentFolder.length})
              </button>
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setFilter(category)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors capitalize ${
                    filter === category
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {category} ({documentsInCurrentFolder.filter(doc => doc.category === category).length})
                </button>
              ))}
            </div>
          </div>
        )}

        {/* File Type Filter */}
        {documentsInCurrentFolder.length > 0 && (
          <div className="bg-white rounded-lg shadow p-4 mb-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Filter by File Type</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFileTypeFilter('all')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  fileTypeFilter === 'all'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Files ({fileTypeCounts.all})
              </button>
              <button
                onClick={() => setFileTypeFilter('pdfs')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  fileTypeFilter === 'pdfs'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                PDFs ({fileTypeCounts.pdfs})
              </button>
              <button
                onClick={() => setFileTypeFilter('docs')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  fileTypeFilter === 'docs'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Documents ({fileTypeCounts.docs})
              </button>
              <button
                onClick={() => setFileTypeFilter('spreadsheets')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  fileTypeFilter === 'spreadsheets'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Spreadsheets ({fileTypeCounts.spreadsheets})
              </button>
              <button
                onClick={() => setFileTypeFilter('images')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  fileTypeFilter === 'images'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Images ({fileTypeCounts.images})
              </button>
            </div>
          </div>
        )}

        {/* Documents List */}
        <div className="bg-white rounded-lg shadow">
          {filteredDocuments.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {documents.length === 0
                  ? 'No documents available'
                  : 'No documents match your filters'}
              </h3>
              <p className="text-gray-600">
                {documents.length === 0
                  ? 'Check back later for new documents.'
                  : 'Try selecting different filters.'}
              </p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
              {filteredDocuments.map((doc) => (
                <div
                  key={doc.id}
                  onClick={() => handlePreviewDocument(doc)}
                  className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all cursor-pointer group p-4"
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getFileIconColor(doc)}`}>
                      <span className="text-xs font-bold uppercase">{getFileExtension(doc)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-1.5">
                        <h3 className="font-semibold text-sm text-gray-900 truncate group-hover:text-primary-600 transition-colors">{doc.title}</h3>
                        {doc.is_pinned && <span className="text-yellow-500 flex-shrink-0"><PinIcon /></span>}
                      </div>
                      <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full font-medium ${getCategoryColor(doc.category)}`}>{doc.category}</span>
                    </div>
                  </div>
                  {doc.description && <p className="text-xs text-gray-500 mb-3 line-clamp-2 leading-relaxed">{doc.description}</p>}
                  <div className="flex items-center justify-between text-xs text-gray-400 pt-3 border-t border-gray-100">
                    <span className="truncate">{doc.uploader_name ? `${doc.uploader_name} ${doc.uploader_lastname}` : ''}</span>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      {doc.file_size_mb && <span>{doc.file_size_mb} MB</span>}
                      <span className="text-gray-300">·</span>
                      <span>{new Date(doc.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredDocuments.map((doc) => (
                <div key={doc.id} className="p-4 sm:p-6 hover:bg-gray-50">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-start space-x-3 sm:space-x-4 flex-1 min-w-0">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getFileIconColor(doc)}`}>
                        <span className="text-xs font-bold uppercase">{getFileExtension(doc)}</span>
                      </div>

                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">{doc.title}</h3>
                          {doc.is_pinned && (
                            <span className="text-yellow-500 flex-shrink-0"><PinIcon /></span>
                          )}
                          <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${getCategoryColor(doc.category)}`}>
                            {doc.category}
                          </span>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${getVisibilityColor(doc.visibility)}`}>
                            {doc.visibility.replace('_', ' ')}
                          </span>
                          {doc.document_type === 'embedded' && (
                            <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                              doc.embed_url && doc.embed_url.includes('google.com')
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-indigo-100 text-indigo-800'
                            }`}>
                              {doc.embed_url && doc.embed_url.includes('google.com') ? 'Google' : 'OneDrive'}
                            </span>
                          )}
                        </div>

                        <div className="text-xs sm:text-sm text-gray-600 space-y-1">
                          {doc.description && (
                            <p className="mb-2 break-words line-clamp-3">{doc.description}</p>
                          )}
                          <div className="flex flex-wrap gap-x-3 gap-y-1">
                            {doc.document_type === 'embedded' ? (
                              <span className="whitespace-nowrap text-indigo-600">Linked Document</span>
                            ) : (
                              <>
                                <span className="whitespace-nowrap">{doc.file_name}</span>
                                <span className="whitespace-nowrap">{doc.file_size_mb} MB</span>
                              </>
                            )}
                            <span className="whitespace-nowrap">{new Date(doc.created_at).toLocaleDateString()}</span>
                            {doc.uploader_name && (
                              <span className="whitespace-nowrap">{doc.uploader_name} {doc.uploader_lastname}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-row sm:flex-col gap-2 sm:ml-4">
                      <button
                        onClick={() => handlePreviewDocument(doc)}
                        className={`flex-1 sm:flex-none ${doc.document_type === 'embedded' ? 'bg-primary-600 hover:bg-primary-700' : 'bg-gray-600 hover:bg-gray-700'} text-white px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap`}
                      >
                        {doc.document_type === 'embedded' ? 'Open' : 'Preview'}
                      </button>
                      {doc.document_type !== 'embedded' && (
                        <button
                          onClick={() => handleDownload(doc.id, doc.file_name)}
                          className="flex-1 sm:flex-none bg-primary-600 hover:bg-primary-700 text-white px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap"
                        >
                          Download
                        </button>
                      )}
                      {doc.uploaded_by === user?.id && (
                        <button
                          onClick={() => handleDelete(doc.id, doc.title)}
                          className="flex-1 sm:flex-none bg-danger-600 hover:bg-danger-700 text-white px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
          </div> {/* Close Documents Content Area */}
        </div> {/* Close Flex Container */}
      </div>

      {/* Document Preview Modal */}
      {documentPreview.isOpen && documentPreview.document && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {documentPreview.document.title}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {documentPreview.document.document_type === 'embedded'
                    ? 'Linked Document'
                    : `${documentPreview.document.file_name} \u2022 ${documentPreview.document.file_size_mb} MB`
                  }
                </p>
              </div>
              <button
                onClick={closeDocumentPreview}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[70vh]">

              {/* Embedded Document View */}
              {documentPreview.document.document_type === 'embedded' ? (
                <div>
                  {/* Password section */}
                  {documentPreview.document.embed_password && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-yellow-800 font-medium mb-2">
                        This document is password-protected. Copy the password below when prompted:
                      </p>
                      <div className="flex items-center gap-2">
                        <code className="bg-white px-3 py-2 rounded border border-yellow-300 text-sm font-mono flex-1 text-gray-900">
                          {documentPreview.document.embed_password}
                        </code>
                        <button
                          onClick={() => handleCopyPassword(documentPreview.document.embed_password)}
                          className={`px-4 py-2 rounded text-sm font-medium transition-colors whitespace-nowrap ${
                            passwordCopied
                              ? 'bg-success-600 text-white'
                              : 'bg-warning-600 hover:bg-warning-700 text-white'
                          }`}
                        >
                          {passwordCopied ? 'Copied!' : 'Copy Password'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* iframe embed */}
                  <div className="bg-gray-100 rounded-lg overflow-hidden">
                    <iframe
                      src={convertToEmbedUrl(documentPreview.document.embed_url)}
                      className="w-full border-0"
                      style={{ height: '600px' }}
                      title={documentPreview.document.title}
                      sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                    />
                  </div>

                  {/* Fallback link */}
                  <div className="mt-3 text-center">
                    <p className="text-sm text-gray-500 mb-2">
                      If the document doesn't load above, open it directly:
                    </p>
                    <a
                      href={documentPreview.document.embed_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm font-medium transition-colors"
                    >
                      Open in New Tab
                    </a>
                  </div>
                </div>
              ) : (
                <>
              {/* Document Info (uploaded files only) */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Document Details</h4>
                    <dl className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-gray-600">Category:</dt>
                        <dd className="text-gray-900 capitalize">{documentPreview.document.category}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-600">Size:</dt>
                        <dd className="text-gray-900">{documentPreview.document.file_size_mb} MB</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-600">Upload Date:</dt>
                        <dd className="text-gray-900">{formatDate(documentPreview.document.created_at)}</dd>
                      </div>
                      {documentPreview.document.uploader_name && (
                        <div className="flex justify-between">
                          <dt className="text-gray-600">Uploaded by:</dt>
                          <dd className="text-gray-900">
                            {documentPreview.document.uploader_name} {documentPreview.document.uploader_lastname}
                          </dd>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <dt className="text-gray-600">Visibility:</dt>
                        <dd className="text-gray-900 capitalize">{documentPreview.document.visibility.replace('_', ' ')}</dd>
                      </div>
                    </dl>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                    <p className="text-sm text-gray-700">
                      {documentPreview.document.description || 'No description provided.'}
                    </p>
                    {documentPreview.document.is_pinned && (
                      <div className="mt-3">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Pinned Document
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Document Preview (uploaded files) */}
              {documentPreview.loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                  <p className="ml-4 text-gray-600">Loading preview...</p>
                </div>
              ) : documentPreview.previewUrl ? (
                <div className="bg-gray-100 rounded-lg overflow-hidden">
                  {documentPreview.document.file_type && documentPreview.document.file_type.includes('pdf') && (
                    <iframe
                      src={documentPreview.previewUrl}
                      className="w-full h-[500px] border-0"
                      title="PDF Preview"
                    />
                  )}

                  {documentPreview.document.file_type && documentPreview.document.file_type.includes('image') && (
                    <img
                      src={documentPreview.previewUrl}
                      alt={documentPreview.document.title}
                      className="w-full h-auto max-h-[500px] object-contain"
                    />
                  )}

                  {documentPreview.document.file_type &&
                   !documentPreview.document.file_type.includes('pdf') &&
                   !documentPreview.document.file_type.includes('image') && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
                      <h4 className="text-blue-800 font-medium mb-2">Preview Not Available</h4>
                      <p className="text-blue-700 text-sm">
                        Preview is not available for this file type. Please download the document to view its contents.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="text-blue-600 mr-3 text-lg">i</div>
                    <div>
                      <h4 className="text-blue-800 font-medium">Document Preview</h4>
                      <p className="text-blue-700 text-sm mt-1">
                        Unable to load preview. Please download the document to view its contents.
                      </p>
                    </div>
                  </div>
                </div>
              )}
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
              <button
                onClick={closeDocumentPreview}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Close
              </button>
              {documentPreview.document.document_type === 'embedded' ? (
                <a
                  href={documentPreview.document.embed_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors text-sm font-medium"
                >
                  Open in New Tab
                </a>
              ) : (
                <button
                  onClick={() => {
                    handleDownload(documentPreview.document.id, documentPreview.document.file_name);
                    closeDocumentPreview();
                  }}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors"
                >
                  Download Document
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      </main>
    </div>
  );
};

export default Documents;